import produce from 'immer'
import { atom } from 'jotai'
import { atomWithReducer } from 'jotai/utils'
import { atomFamily } from 'jotai-family'
import {
  Choice,
  createEmpty as createEmptyChoice,
} from 'src/legacy_modules/entities/choice/entity'
import {
  MediumItem,
  createEmpty as createEmptyMediumItem,
} from 'src/legacy_modules/entities/medium-item/entity'
import {
  SmallItem,
  createEmpty as createEmptySmallItem,
} from 'src/legacy_modules/entities/small-item/entity'
import { Template } from 'src/legacy_modules/entities/template/entity'
import { assertNever } from 'src/utils/assertNever'

import { hasChoice } from '../../../../../../util/item-type-classification'

type State = {
  originalTemplate: Template | null
  title: string
  videoEnabled: boolean
  mediumItems: MediumItem[]
  submitting: boolean
}

type Action =
  | { type: 'initWithTemplate'; template: Template }
  | { type: 'updateTitle'; title: string }
  | { type: 'updateVideoEnabled'; videoEnabled: boolean }
  | { type: 'addMediumItem' }
  | { type: 'deleteMediumItem'; index: number }
  | {
      type: 'changeIndexMediumItem'
      index: number
      direction: 'up' | 'down'
    }
  | { type: 'updateMediumItem'; index: number; values: Partial<MediumItem> }
  | { type: 'addSmallItem'; mediumItemIndex: number; v2: boolean }
  | { type: 'deleteSmallItem'; mediumItemIndex: number; uid: string }
  | {
      type: 'changeIndexSmallItem'
      mediumItemIndex: number
      uid: string
      direction: 'up' | 'down'
    }
  | {
      type: 'updateSmallItem'
      mediumItemIndex: number
      uid: string
      values: Partial<SmallItem>
    }
  | {
      type: 'updateSmallItemViewableHospitalUids'
      smallItemUids: string[]
      trialHospitalUids: string[]
    }
  | { type: 'addChoice'; mediumItemIndex: number; smallItemUid: string }
  | {
      type: 'updateChoice'
      mediumItemIndex: number
      smallItemUid: string
      choiceIndex: number
      values: Partial<Choice>
    }
  | {
      type: 'deleteChoice'
      mediumItemIndex: number
      smallItemUid: string
      choiceIndex: number
    }
  | {
      type: 'addSmallItemToChoice'
      mediumItemIndex: number
      smallItemUid: string
      choiceIndex: number
      v2: boolean
    }
  | { type: 'submitStarted' }
  | { type: 'submitDone' }
  | { type: 'submitFailed' }

const initialState: State = {
  originalTemplate: null,
  title: '',
  videoEnabled: false,
  mediumItems: [],

  submitting: false,
}

const reducer = (state: State, action: Action): State =>
  produce(state, draft => {
    switch (action.type) {
      case 'initWithTemplate': {
        const { mediumItems } = action.template
        if (!mediumItems) return

        draft.originalTemplate = action.template
        draft.title = action.template.title
        draft.videoEnabled = action.template.videoEnabled
        draft.mediumItems = mediumItems
        return
      }
      case 'updateTitle': {
        draft.title = action.title
        return
      }
      case 'updateVideoEnabled': {
        draft.videoEnabled = action.videoEnabled
        return
      }
      case 'addMediumItem': {
        const empty = createEmptyMediumItem()
        draft.mediumItems.push(empty)
        return
      }
      case 'deleteMediumItem': {
        draft.mediumItems.splice(action.index, 1)
        return
      }
      case 'changeIndexMediumItem': {
        const { index, direction } = action
        const swappedIndex = direction === 'up' ? index - 1 : index + 1
        if (swappedIndex < 0 || swappedIndex >= state.mediumItems.length) return
        ;[draft.mediumItems[index], draft.mediumItems[swappedIndex]] = [
          draft.mediumItems[swappedIndex],
          draft.mediumItems[index],
        ]
        return
      }
      case 'updateMediumItem': {
        const mediumItem = draft.mediumItems[action.index]
        if (!mediumItem) return

        for (const key in action.values) {
          const k = key as keyof MediumItem
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(mediumItem[k] as any) = action.values[k] as any
        }
        return
      }
      case 'addSmallItem': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const empty = createEmptySmallItem(action.v2)
        empty.index = Object.values(mediumItem.smallItemMap).filter(
          si => si.parentUid === null,
        ).length
        mediumItem.smallItemMap[empty.uid] = empty
        return
      }
      case 'deleteSmallItem': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.uid]

        // 親の削除
        if (smallItem.parentUid) {
          const parent = mediumItem.smallItemMap[smallItem.parentUid]
          const choice = parent.choices.find(c =>
            c.smallItemUids.includes(smallItem.uid),
          )!
          choice.smallItemUids.splice(
            choice.smallItemUids.findIndex(uid => uid === smallItem.uid),
            1,
          )
        }

        // 子の削除
        deleteSmallItem(mediumItem, smallItem)

        delete mediumItem.smallItemMap[action.uid]

        // 残ったフィールドの index に抜けが無いようにする
        Object.values(mediumItem.smallItemMap)
          .filter(si => si.parentUid === null)
          .sort((a, b) => a.index - b.index)
          .forEach((si, idx) => {
            si.index = idx
          })
        return
      }
      case 'changeIndexSmallItem': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const { uid, direction } = action
        const targetSmallItem = mediumItem.smallItemMap[uid]

        const rootSmallItems = Object.values(mediumItem.smallItemMap)
          .filter(si => si.parentUid === null)
          .sort((a, b) => a.index - b.index)
        const targetSmallItemIndex = rootSmallItems.findIndex(
          si => si.uid === targetSmallItem.uid,
        )

        const swappedIndex =
          direction === 'up'
            ? targetSmallItemIndex - 1
            : targetSmallItemIndex + 1

        const swappedItem = rootSmallItems[swappedIndex]
        if (!swappedItem) return
        ;[targetSmallItem.index, swappedItem.index] = [
          swappedItem.index,
          targetSmallItem.index,
        ]
        return
      }
      case 'updateSmallItem': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.uid]

        for (const key in action.values) {
          const k = key as keyof SmallItem
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(smallItem[k] as any) = action.values[k] as any
        }
        return
      }
      case 'updateSmallItemViewableHospitalUids': {
        draft.submitting = false

        action.smallItemUids.forEach(uid => {
          const origMediumItem = draft.originalTemplate?.mediumItems?.find(
            mi => !!mi.smallItemMap[uid],
          )
          if (!origMediumItem) return

          const origSmallItem = origMediumItem.smallItemMap[uid]
          if (!origSmallItem) return

          const mediumItem = draft.mediumItems.find(
            mi => !!mi.smallItemMap[uid],
          )
          if (!mediumItem) return

          const smallItem = mediumItem.smallItemMap[uid]
          if (!smallItem) return

          origSmallItem.viewableHospitalUids = action.trialHospitalUids
          smallItem.viewableHospitalUids = action.trialHospitalUids
        })
        return
      }
      case 'addChoice': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.smallItemUid]

        if (!hasChoice(smallItem)) return

        const empty = createEmptyChoice()
        smallItem.choices.push(empty)
        return
      }
      case 'deleteChoice': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.smallItemUid]

        if (!hasChoice(smallItem)) return

        const choice = smallItem.choices[action.choiceIndex]
        choice.smallItemUids
          .map(uid => mediumItem.smallItemMap[uid])
          .forEach(si => deleteSmallItem(mediumItem, si))

        smallItem.choices.splice(action.choiceIndex, 1)
        return
      }
      case 'addSmallItemToChoice': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.smallItemUid]

        if (!hasChoice(smallItem)) return

        const newSmallItem = createEmptySmallItem(action.v2)
        newSmallItem.parentUid = smallItem.uid
        mediumItem.smallItemMap[newSmallItem.uid] = newSmallItem
        smallItem.choices[action.choiceIndex].smallItemUids.push(
          newSmallItem.uid,
        )
        return
      }
      case 'updateChoice': {
        const mediumItem = draft.mediumItems[action.mediumItemIndex]
        if (!mediumItem) return

        const smallItem = mediumItem.smallItemMap[action.smallItemUid]

        const choice = smallItem.choices[action.choiceIndex]

        for (const key in action.values) {
          const k = key as keyof Choice
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(choice[k] as any) = action.values[k] as any
        }
        return
      }
      case 'submitStarted': {
        draft.submitting = true
        return
      }
      case 'submitDone': {
        draft.submitting = false
        return
      }
      case 'submitFailed': {
        draft.submitting = false
        return
      }
      default: {
        return assertNever(action)
      }
    }
  })

// SmallItem と、その choices 以下の SmallItem の削除
const deleteSmallItem = (mediumItem: MediumItem, smallItem: SmallItem) => {
  const deleteChildren = (si: SmallItem) => {
    const childSmallItemUids = si.choices.map(c => c.smallItemUids).flat()

    childSmallItemUids.forEach(childSmallItemUid => {
      const childSmallitem = mediumItem.smallItemMap[childSmallItemUid]
      deleteChildren(childSmallitem)
      delete mediumItem.smallItemMap[childSmallItemUid]
    })
  }

  deleteChildren(smallItem)
  delete mediumItem.smallItemMap[smallItem.uid]
}

export const templateDetailAtom = atomWithReducer(initialState, reducer)

export const smallItemsAtomFamily = atomFamily((mediumItemUid: string) =>
  atom(get => {
    const state = get(templateDetailAtom)
    const mediumItem = state.mediumItems.find(mi => mi.uid === mediumItemUid)
    if (!mediumItem) return []

    return Object.values(mediumItem.smallItemMap)
      .filter(si => si.parentUid === null)
      .sort((a, b) => a.index - b.index)
  }),
)
